import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { Pencil, Plus, RefreshCw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { toast } from '@/components/ui/toast';
import { BILLING_SFTP_CONFIG, BILLING_SFTP_TYPES, SAVE_BILLING_SFTP_CONFIG, SAVE_BILLING_SFTP_TYPE, SAVE_BILLING_SFTP_TYPES } from './queries';
import type { BillingSftpConfig, BillingSftpType } from './types';

type ConfigForm = Omit<BillingSftpConfig, 'passwordConfigured'> & { password: string };
interface TypeForm { id?: number; name: string; direction: 'incoming' | 'outgoing'; path: string; host: string; enabled: boolean }
const newType: TypeForm = { name: '', direction: 'incoming', path: '', host: '', enabled: true };

const serializeTypes = (types: BillingSftpType[]): string => JSON.stringify(types.map(type => ({
  id: type.id, name: type.name, direction: type.direction, path: type.path, host: type.host, enabled: type.enabled,
})));

const parseTypes = (text: string): TypeForm[] => {
  const value: unknown = JSON.parse(text);
  if (!Array.isArray(value) || !value.length || value.length > 100) throw new Error('Enter an array of 1 to 100 file types');
  const names = new Set<string>();
  const types: TypeForm[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('Invalid file type');
    const type = entry as Record<string, unknown>;
    if (typeof type.name !== 'string' || !type.name.trim() || type.name.trim().length > 100
      || !['incoming', 'outgoing'].includes(String(type.direction))
      || typeof type.path !== 'string' || !type.path.trim() || type.path.trim().length > 255
      || typeof type.host !== 'string' || !type.host.trim() || type.host.trim().length > 253
      || (type.id !== undefined && (typeof type.id !== 'number' || !Number.isInteger(type.id) || type.id <= 0 || type.id > 2147483647))
      || (type.enabled !== undefined && typeof type.enabled !== 'boolean')) throw new Error('Invalid file type');
    const name = type.name.trim();
    if (names.has(name)) throw new Error('Duplicate type name');
    names.add(name);
    types.push({ ...(type.id !== undefined ? { id: type.id as number } : {}), name, direction: type.direction as TypeForm['direction'], path: type.path.trim(),
      host: type.host.trim().toLowerCase(), enabled: type.enabled !== false });
  }
  return types;
};

export const BillingSftpAdmin = () => {
  const configQuery = useQuery<{ billingSftpConfig: BillingSftpConfig }>(BILLING_SFTP_CONFIG, { fetchPolicy: 'network-only' });
  const typesQuery = useQuery<{ billingSftpTypes: BillingSftpType[] }>(BILLING_SFTP_TYPES);
  const [saveConfig, configMutation] = useMutation<{ saveBillingSftpConfig: BillingSftpConfig }>(SAVE_BILLING_SFTP_CONFIG);
  const [saveType, typeMutation] = useMutation(SAVE_BILLING_SFTP_TYPE);
  const [saveTypes] = useMutation<{ saveBillingSftpTypes: BillingSftpType[] }, { types: TypeForm[] }>(SAVE_BILLING_SFTP_TYPES);
  const [typeDialog, setTypeDialog] = useState(false);
  const [typesJson, setTypesJson] = useState('');
  const [importing, setImporting] = useState(false);
  const typesDirty = useRef(false);
  const storedTypesJson = serializeTypes(typesQuery.data?.billingSftpTypes || []);

  useEffect(() => {
    if (typesDirty.current) return;
    setTypesJson(storedTypesJson);
  }, [storedTypesJson]);
  const configForm = useForm<ConfigForm>();
  const typeForm = useForm<TypeForm>({ defaultValues: newType });
  const { reset } = configForm;
  const config = configQuery.data?.billingSftpConfig;

  useEffect(() => {
    if (!config) return;
    reset({ version: config.version, bucket: config.bucket, host: config.host, port: config.port,
      username: config.username, hostKeySha256: config.hostKeySha256, password: '' });
  }, [config, reset]);

  const submitConfig = configForm.handleSubmit(async values => {
    try {
      await saveConfig({ variables: { input: values } });
      configForm.setValue('password', '');
      await configQuery.refetch();
      toast.success('SFTP settings saved');
    } catch { toast.error('Unable to save settings. Refresh and check pending uploads and connection details'); }
  });
  const openType = (type?: BillingSftpType) => {
    typeForm.reset(type ? { id: type.id, name: type.name, direction: type.direction, path: type.path, host: type.host, enabled: type.enabled } : { ...newType, host: config?.host || '' });
    setTypeDialog(true);
  };
  const submitType = typeForm.handleSubmit(async values => {
    try {
      await saveType({ variables: { input: values } });
      await typesQuery.refetch();
      setTypeDialog(false);
      toast.success('File type saved');
    } catch { toast.error('Unable to save file type. Check the name and folder mappings for conflicts'); }
  });
  const importTypes = async () => {
    setImporting(true);
    try {
      const entries = parseTypes(typesJson);
      const result = await saveTypes({ variables: { types: entries } });
      if (!result.data?.saveBillingSftpTypes) throw new Error('No saved file types returned');
      typesDirty.current = false;
      setTypesJson(serializeTypes(result.data.saveBillingSftpTypes));
      toast.success(`${entries.length} file types saved`);
      await typesQuery.refetch().catch(() => toast.error('File types saved. Refresh to reload the list'));
    } catch {
      toast.error('Unable to save file types. Check the JSON for required names, directions, paths, hosts, and conflicts');
    } finally { setImporting(false); }
  };
  const columns: UnifiedTableColumn<BillingSftpType>[] = [
    { key: 'name', header: 'Type', field: 'name' },
    { key: 'direction', header: 'Direction', field: 'direction' },
    { key: 'path', header: 'Path', field: 'path' },
    { key: 'host', header: 'Host', field: 'host' },
    { key: 'enabled', header: 'Status', accessor: type => type.enabled ? 'Enabled' : 'Disabled' },
  ];

  return <div className="space-y-4 p-4 text-[var(--neutral-gray-120)]">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-xl font-semibold">SFTP Administration</h2>
      <Button id="billing-sftp-admin-refresh" variant="outline" onClick={() => { void configQuery.refetch(); void typesQuery.refetch(); }} disabled={configQuery.loading || typesQuery.loading}>
        <RefreshCw className="mr-2 h-4 w-4" />Refresh
      </Button>
    </div>
    <p className="text-sm text-[var(--neutral-gray-90)]">Transfers run every 10 minutes when the Billing SFTP job is enabled in Cron Management. Files stay stored when archived.</p>
    {configQuery.loading && <p role="status" className="text-sm">Loading connection settings…</p>}
    {configQuery.error && <p role="alert" className="text-sm text-[var(--semantic-error)]">Unable to load SFTP settings. Storage must be provisioned before configuration.</p>}
    {config && <Card className="p-4">
      <h3 className="mb-3 text-base font-semibold">Connection and storage</h3>
      <form onSubmit={submitConfig} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><label htmlFor="billing-sftp-admin-host" className="mb-1 block text-sm font-medium">SFTP hostname or IP address</label>
            <Input id="billing-sftp-admin-host" {...configForm.register('host', { required: true })} required autoComplete="off" /></div>
          <div><label htmlFor="billing-sftp-admin-port" className="mb-1 block text-sm font-medium">Port</label>
            <Input id="billing-sftp-admin-port" type="number" min={1} max={65535} {...configForm.register('port', { required: true, valueAsNumber: true })} required /></div>
          <div><label htmlFor="billing-sftp-admin-username" className="mb-1 block text-sm font-medium">Username</label>
            <Input id="billing-sftp-admin-username" {...configForm.register('username', { required: true })} required maxLength={100} autoComplete="off" /></div>
          <div><label htmlFor="billing-sftp-admin-password" className="mb-1 block text-sm font-medium">{config.passwordConfigured ? 'Replace password' : 'Password'}</label>
            <Input id="billing-sftp-admin-password" type="password" {...configForm.register('password')} required={!config.passwordConfigured} maxLength={1024} autoComplete="new-password" />
            <p className="mt-1 text-xs text-[var(--neutral-gray-90)]">{config.passwordConfigured ? 'Password configured. Leave blank to keep it.' : 'The password is stored encrypted.'}</p></div>
          <div><label htmlFor="billing-sftp-admin-bucket" className="mb-1 block text-sm font-medium">S3 bucket</label>
            <Input id="billing-sftp-admin-bucket" {...configForm.register('bucket', { required: true })} required maxLength={63} />
            <p className="mt-1 text-xs text-[var(--neutral-gray-90)]">Folder prefix: ecm/communications/{config.host}/</p></div>
          <div><label htmlFor="billing-sftp-admin-host-key" className="mb-1 block text-sm font-medium">Trusted host key SHA256 fingerprint</label>
            <Input id="billing-sftp-admin-host-key" {...configForm.register('hostKeySha256', { required: true, pattern: /^[a-fA-F0-9]{64}$/ })} required pattern="[a-fA-F0-9]{64}" maxLength={64} autoComplete="off" />
            <p className="mt-1 text-xs text-[var(--neutral-gray-90)]">Enter the 64-character hex fingerprint supplied by the SFTP administrator.</p></div>
        </div>
        <Button id="billing-sftp-admin-save" type="submit" disabled={configMutation.loading || configQuery.loading}><Save className="mr-2 h-4 w-4" />{configMutation.loading ? 'Saving…' : 'Save connection'}</Button>
      </form>
    </Card>}
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold">File types and folders</h3>
        <Button id="billing-sftp-types-add" disabled={importing} onClick={() => openType()}><Plus className="mr-2 h-4 w-4" />Add type</Button>
      </div>
      {typesQuery.error && <p role="alert" className="mb-3 text-sm text-[var(--semantic-error)]">Unable to load file types.</p>}
      <UnifiedTable columns={columns} data={typesQuery.data?.billingSftpTypes || []} rowKey={type => String(type.id)} density="compact" loading={typesQuery.loading} showRowActions
        emptyTitle="No file types configured" emptyDescription="Add a file type."
        renderRowActions={type => <Button id={`billing-sftp-types-edit-${type.id}`} size="sm" variant="ghost" disabled={importing} onClick={() => openType(type)}><Pencil className="mr-1 h-4 w-4" />Edit</Button>} />
      <div className="mt-3 space-y-2">
        <label htmlFor="billing-sftp-types-json" className="block text-sm font-medium">File types JSON</label>
        <Textarea id="billing-sftp-types-json" value={typesJson} onChange={event => { typesDirty.current = true; setTypesJson(event.target.value); }} disabled={importing} rows={3} />
        <Button id="billing-sftp-types-import" disabled={importing || typeMutation.loading || typesQuery.loading || !typesJson.trim()} onClick={() => void importTypes()}>
          <Save className="mr-2 h-4 w-4" />{importing ? 'Saving…' : 'Save types'}
        </Button>
      </div>
    </Card>
    <Dialog open={typeDialog} onOpenChange={(open: boolean) => { if (!typeMutation.loading) setTypeDialog(open); }}>
      <DialogContent aria-describedby={undefined} contentWidth="560px" contentHeight="auto" className="flex flex-col">
        <DialogHeader><DialogTitle>{typeForm.getValues('id') ? 'Edit file type' : 'Add file type'}</DialogTitle></DialogHeader>
        <form onSubmit={submitType} className="flex min-h-0 flex-col gap-4">
          <div className="space-y-3 overflow-y-auto">
            <div><label htmlFor="billing-sftp-type-name" className="mb-1 block text-sm font-medium">Type name</label><Input id="billing-sftp-type-name" {...typeForm.register('name', { required: true })} required maxLength={100} /></div>
            <div><label htmlFor="billing-sftp-type-direction" className="mb-1 block text-sm font-medium">Direction</label>
              <Controller name="direction" control={typeForm.control} rules={{ required: true }} render={({ field }) =>
                <Select value={field.value} onValueChange={field.onChange}><SelectTrigger id="billing-sftp-type-direction"><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem id="billing-sftp-type-direction-incoming" value="incoming">Incoming</SelectItem>
                  <SelectItem id="billing-sftp-type-direction-outgoing" value="outgoing">Outgoing</SelectItem>
                </SelectContent></Select>} />
            </div>
            <div><label htmlFor="billing-sftp-type-path" className="mb-1 block text-sm font-medium">Path</label><Input id="billing-sftp-type-path" {...typeForm.register('path', { required: true })} required placeholder="/outfile/ecm/ADT" maxLength={255} /></div>
            <div><label htmlFor="billing-sftp-type-host" className="mb-1 block text-sm font-medium">Host</label><Input id="billing-sftp-type-host" {...typeForm.register('host', { required: true })} required maxLength={253} autoComplete="off" /></div>
            <Controller name="enabled" control={typeForm.control} render={({ field }) => <div className="flex items-center gap-2"><Checkbox id="billing-sftp-type-enabled" checked={field.value} onCheckedChange={value => field.onChange(value === true)} /><label htmlFor="billing-sftp-type-enabled" className="text-sm">Enabled</label></div>} />
          </div>
          <DialogFooter><Button id="billing-sftp-type-save" type="submit" disabled={typeMutation.loading}><Save className="mr-2 h-4 w-4" />Save type</Button><Button id="billing-sftp-type-cancel" type="button" variant="outline" disabled={typeMutation.loading} onClick={() => setTypeDialog(false)}>Cancel</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </div>;
};
